<template>
<div>
    <div class="grid grid-cols-4 gap-4 m-4">
        <div class="col-span-1">
            <input type="text" placeholder="Asset Number, e.g. SYM002" v-model="search" class="py-1 px-2 rounded mb-1 w-9/12">
            <div class="mt-4 h-scroll overflow-auto scroll-smooth snap-y bg-gray-100 rounded">
                <div v-if="searching">
                    <loader :show="true"></loader>
                </div>
                <div v-else-if="devicesNames.length === 0">
                    <p class="m-3 font-bold">No such devices</p>
                </div>
                <div v-else>
                    <div v-for="assetNumber in devicesNames" :key="assetNumber" 
                        @click="selectDevice(assetNumber)" 
                        class="hover:bg-gray-200 cursor-pointer p-2 m-1 rounded" 
                        :class="{ 'bg-gray-300': selectedDevice.uniId === assetNumber }"
                    >
                        {{ assetNumber }}
                    </div>
                </div>
            </div>
        </div>
        <div class="col-span-3 bg-white p-4 rounded">
            <div v-if="wait">
                <loader :show="true"></loader>
            </div>
            <div v-else-if="selectedDevice.uniId !== undefined" class="m-6">
                <div>
                  <ul class="my-4 flex space-x-2">
                    <li class="btn-report transition" :class="current_tab === $options.GENERAL_INFO ? 'sparkYellow' : 'pureNavy'" @click="setTab(false, $options.GENERAL_INFO)">{{ $t('general_info') }}</li>
                    <li v-if="hasServicePermissions('Service Tickets')" class="btn-report transition" :class="ticketHistoryClass" @click="setTab(ticketCount === 0, $options.TICKET_HISTORY)">{{ $t('ticket_history') }} ({{ ticketCount }})</li>
                    <li class="btn-report transition" :class="chartsClass" @click="setTab(chartsCount === 0, $options.DEVICE_CHARTS)">{{ $t('charts') }}</li>
                  </ul>
                </div>
                <div v-if="current_tab === $options.GENERAL_INFO">
                  <div>
                    <feather type="smartphone" class="mx-1 w-6 relative top-1"/>
                    <span class="text-xl font-bold mx-1 uppercase">
                        <span class="font-bold">{{ selectedDevice.uniId }}</span>
                    </span>
                  </div>

                  <hr class="h-px my-4 bg-gray-200 border-0">

                  <div class="p-2 my-2">
                    <p class="text-lg font-light mb-6">General information</p>
                    <div class="flex flex-wrap">
                      <DeviceProperty assetPropety='Name' :assetValue="selectedDevice['Name']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Serial Number' :assetValue="selectedDevice['SerialNumber']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='IMEI' :assetValue="selectedDevice['IMEI_Number__c']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Model' :assetValue="selectedDevice['Model_Name__c']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Status' :assetValue="selectedDevice['Status']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Last Seen' :assetValue="formatDateObject(selectedDevice['date'])"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='Contract Number' :assetValue="isEmpty(selectedDevice['Service_Contract__r']) ? null : selectedDevice['Service_Contract__r']['ContractNumber']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Contract End Date' :assetValue="formatDateObject(selectedDevice['Service_Contract_Expiry_Date__c'], 'DD/MM/YYYY')"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Contract Status' :assetValue="selectedDevice['Service_Contract_Status__c']" :class="statusClass"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Site' :assetValue="isEmpty(selectedDevice['Site__r']) ? null : selectedDevice['Site__r']['Name']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Sim Card' :assetValue="selectedDevice['SIM_Card_New__c.Name']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Voice Number' :assetValue="selectedDevice['SIM_Card_Asset_Voice_Number__c']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='User' :assetValue="selectedDevice['User__c']"></DeviceProperty> <!--SF-->
                      <DeviceProperty assetPropety='Custom' :assetValue="selectedDevice['custom']"></DeviceProperty> <!--SF-->
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0">
                  </div>
                  <div class="p-2 my-2">
                    <p class="text-lg font-light mb-6">Build information</p>
                    <div class="flex flex-wrap">
                      <DeviceProperty assetPropety='Manufacturer' :assetValue="selectedDevice['manufacturer']"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='Model' :assetValue="selectedDevice['model']"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='Security Patch' :assetValue="formatDateObject(selectedDevice['security_patch_date'])"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='OS Version' :assetValue="selectedDevice['os_version']"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='Build No' :assetValue="selectedDevice['build_no']"></DeviceProperty> <!--v2-->
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0">
                  </div>
                  <div class="p-2 my-2">
                    <p class="text-lg font-light mb-6">Connectivity information</p>
                    <div class="flex flex-wrap">
                      <DeviceProperty assetPropety='GSM Name' :assetValue="selectedDevice['gsm_name']"></DeviceProperty>
                      <DeviceProperty assetPropety='GSM Tower' :assetValue="selectedDevice['gsm_cell_tower_id']"></DeviceProperty>
                      <DeviceProperty assetPropety='GSM Signal' :assetValue="selectedDevice['gsm_signal']"></DeviceProperty>
                      <DeviceProperty assetPropety='GSM Download' :assetValue="selectedDevice['gsm_download']"></DeviceProperty>
                      <DeviceProperty assetPropety='GSM Upload' :assetValue="selectedDevice['gsm_upload']"></DeviceProperty>
                      <DeviceProperty assetPropety='Wifi Name' :assetValue="selectedDevice['wifi_name']"></DeviceProperty>
                      <DeviceProperty assetPropety='Wifi Signal' :assetValue="selectedDevice['wifi_signal']"></DeviceProperty>
                      <DeviceProperty assetPropety='WiFi Download' :assetValue="selectedDevice['wifi_download']"></DeviceProperty>
                      <DeviceProperty assetPropety='WiFi Upload' :assetValue="selectedDevice['wifi_upload']"></DeviceProperty>
                      <DeviceProperty assetPropety='Device IP' :assetValue="selectedDevice['device_ip']"></DeviceProperty>
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0">
                  </div>
                  <div class="p-2 my-2">
                    <p class="text-lg font-light mb-6">Storage information</p>
                    <div class="flex flex-wrap">
                      <DeviceProperty assetPropety='Total Internal Storage' :assetValue="selectedDevice['total_internal_storage']"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='Internal Storage' :assetValue="selectedDevice['internal_storage']"></DeviceProperty>
                      <DeviceProperty assetPropety='Total External Storage' :assetValue="selectedDevice['total_external_storage']"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='External Storage' :assetValue="selectedDevice['external_storage']"></DeviceProperty>
                      <DeviceProperty assetPropety='Total Ram' :assetValue="selectedDevice['total_ram']"></DeviceProperty> <!--v2-->
                      <DeviceProperty assetPropety='Ram' :assetValue="selectedDevice['ram']"></DeviceProperty>
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0">
                  </div>
                  <div class="p-2 my-2">
                    <p class="text-lg font-light mb-6">Battery information</p>
                    <div class="flex flex-wrap">
                      <DeviceProperty assetPropety='Battery [%]' :assetValue="selectedDevice['battery']"></DeviceProperty>
                      <DeviceProperty assetPropety='Battery Time Left' :assetValue="selectedDevice['battery_time_left']"></DeviceProperty>
                      <DeviceProperty assetPropety='Battery Temperature' :assetValue="selectedDevice['battery_temperature']"></DeviceProperty>
                      <DeviceProperty assetPropety='Battery Status' :assetValue="selectedDevice['battery_status']"></DeviceProperty>
                      <DeviceProperty assetPropety='Battery Usage' :assetValue="selectedDevice['battery_usage']"></DeviceProperty>
                      <DeviceProperty assetPropety='Status Date' :assetValue="formatDateObject(selectedDevice['date'])"></DeviceProperty>
                      <DeviceProperty assetPropety='Screen Time' :assetValue="selectedDevice['screen_time']"></DeviceProperty>
                      <DeviceProperty assetPropety='Charging' :assetValue="selectedDevice['charging']"></DeviceProperty>
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0">
                  </div>

                  <div v-if="selectedDevice.latitude !== undefined && selectedDevice.latitude !== -200 && selectedDevice.longitude !== -200" class="p-2 my-2">
                    <p class="text-lg font-light mb-6">Device Last Known Position ({{ formatDateObject(selectedDevice['date']) }})</p>
                    <Map :items-payload="[ { id: selectedDevice.uniId, lat: selectedDevice.latitude, long: selectedDevice.longitude } ]"></Map>
                    <hr class="h-px my-4 bg-gray-200 border-0">
                  </div>

                  <div v-if="selectedDevice.tickets !== undefined && selectedDevice.tickets.length > 0" class="p-2 my-2">
                    <p class="text-lg font-light mb-6">Device Tickets (last 30 days)</p>
                    <div class="flex flex-wrap">
                        <table class="w-full table-fixed">
                            <thead>
                                <tr>
                                    <th>Number</th>
                                    <th>Status</th>
                                    <th>FAO</th>
                                    <th>Street</th>
                                    <th>City</th>
                                    <th>State</th>
                                    <th>Country</th>
                                    <th>Postcode</th>
                                    <th>Tracking No</th>
                                    <th>Site</th>
                                    <th>Delivery Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="ticket in selectedDevice.tickets" :key="ticket.CaseNumber">
                                    <td> {{ ticket['CaseNumber'] }} </td>
                                    <td> {{ ticket['Status'] }} </td>
                                    <td> {{ ticket['Contact_FAO__c'] }} </td>
                                    <td> {{ ticket['Address_Line_1__c'] }} </td>
                                    <td> {{ ticket['Address_Line_2__c'] }} </td>
                                    <td> {{ ticket['Address_Line_3__c'] }} </td>
                                    <td> {{ ticket['Address_Line_4__c'] }} </td>
                                    <td> {{ ticket['Postcode__c'] }} </td>
                                    <td> {{ ticket['Collection_Tracking_Number__c'] }} </td>
                                    <td> {{ ticket['SiteName__r'] ? ticket['SiteName__r']['Name'] : '' }} </td>
                                    <td> {{ ticket['Expected_Delivery_Date__c'] }} </td>
                                </tr>
                            </tbody>
                            </table>
                    </div>
                    <hr class="h-px my-4 bg-gray-200 border-0">
                  </div>
                </div>
                <TicketHistory v-if="current_tab === $options.TICKET_HISTORY"/>
                <DeviceCharts v-if="current_tab === $options.DEVICE_CHARTS"/>
            </div>
            <div v-else>
                <div class="m-6">
                    <p class="font-bold">Select device</p>
                    <p>Please use menu on the left</p>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>

import { mapGetters, mapActions } from 'vuex'
import DeviceProperty from '../components/DeviceProperty'
import moment from 'moment'
import Map from '../modules/map/components/Map'
import Loader from '../components/Loader'
import TicketHistory from "@/components/TicketHistory.vue";
import DeviceCharts from "@/components/DeviceCharts.vue";

export default {
    components: {
        DeviceCharts,
        TicketHistory,
        DeviceProperty,
        Map,
        Loader
    },
    GENERAL_INFO: 'general_info',
    TICKET_HISTORY: 'ticket_history',
    DEVICE_CHARTS: 'device_charts',
    data(){
        return {
            current_tab: this.$options.GENERAL_INFO,
            timeoutId: null,
            search: '',
            lastSelected: null
        }
    },
    computed: {
        ...mapGetters([
            'devicesNames',
            'selectedDevice',
            'types',
            'ticketHistory',
            'batteryHealth',
            'hasServicePermissions',
            'searching'
        ]),
        ticketCount() {
            return this.ticketHistory === null ? 0 : this.ticketHistory.length
        },
        ticketHistoryClass() {
            if (this.ticketCount === 0) return 'button-disabled bg-gray-300'
            return this.current_tab === this.$options.TICKET_HISTORY ? 'sparkYellow' : 'pureNavy'
        },
        chartsCount() {
            let count = 0
            if (this.hasServicePermissions('OptimiseBatteryHealthEstimation') && this.batteryHealth) count++
            //future charts here
            return count
        },
        chartsClass() {
            if (this.chartsCount === 0) return 'button-disabled bg-gray-300'
            return this.current_tab === this.$options.DEVICE_CHARTS ? 'sparkYellow' : 'pureNavy'
        },
        wait() {
            return this.lastSelected != null && this.lastSelected != this.selectedDevice['uniId']
        },
        statusClass() {
          let field = this.selectedDevice['Service_Contract_Expiry_Date__c']
          if(!field) {
            return ''
          }

          const dateObj = moment(field)
          if(!dateObj.isValid()) {
            return ''
          }
          let diff = moment('now').diff(dateObj);
          if (diff < 0) {
            return 'expired'
          } else if (diff < 86400000 * 60) {
            return 'expiring'
          } else {
            return 'active'
          }
        },
    },
    methods: {
        ...mapActions([
            'downloadDevices',
            'downloadTypes',
            'downloadExtendedDevices',
            'downloadDeviceTicketHistory',
            'downloadDeviceBatteryHealth'
        ]),
        searchDevices() {
            this.downloadDevices(this.search)
        },
        async selectDevice(assetNumber) {
            if (this.lastSelected === assetNumber) return
            this.lastSelected = assetNumber
            this.current_tab = this.$options.GENERAL_INFO
            await this.downloadExtendedDevices(assetNumber)
            await this.$nextTick()
            this.downloadDeviceTicketHistory(this.selectedDevice.Id)
            if (this.hasServicePermissions('OptimiseBatteryHealthEstimation')) this.downloadDeviceBatteryHealth(this.selectedDevice.asset_number)
        },
        formatDateObject(field, format = 'D MMMM YYYY, H:mm:ss') {

            if(!field) {
                return ''
            }

            const dateObj = moment(field)
            if(!dateObj.isValid()) {
                return field
            }

            return dateObj.format(format)
        },
        setTab(disabled, tabName) {
            if (disabled) return
            this.current_tab = tabName
        },
        isEmpty(element) {
          if(element === undefined || element === null) {
            return true
          }
          return false
        }
    },
    mounted() {
        this.downloadDevices()
        this.downloadTypes()
    },
    watch: {
        search() {
            clearTimeout(this.timeoutId)
            this.timeoutId = setTimeout(() => {
              this.searchDevices()
            }, 550)
        }
    }
}
</script>

<style scoped>
    .h-scroll {
        height: 500px;
    }
    .button-disabled {
      cursor: not-allowed;
      color: #00283a;
    }
    .active {
      color: green;
    }
    .expiring {
      color: #FFBF00;
    }
    .expired {
      color: red;
    }
</style>